import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { SellerRegistryTab } from "@/components/seller/registry-tab";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BadgeCheck,
  BanknoteArrowUp,
  Bell,
  CheckCircle2,
  ClipboardList,
  XCircle,
  Clock,
  Coins,
  CreditCard,
  ExternalLink,
  Eye,
  Gauge,
  Loader2,
  Package,
  RefreshCcw,
  ShieldAlert,
  Sparkles,
  Timer,
  TrendingUp,
  Truck,
  Wallet,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
} from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StripeConnectCard } from "@/components/stripe-connect-card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatGBP } from "@/lib/format";
import {
  getSellerOverview,
  getSellerPerformance,
  createExpressDashboardLink,
} from "@/lib/seller-dashboard.functions";
import { getSellerWatcherCounts } from "@/lib/seller-watchers.functions";
import { FulfilmentTab } from "@/components/seller/fulfilment-tab";
import { SellerHeroGrid } from "@/components/seller/hero-grid";
import { AnalyticsTab } from "@/components/seller/analytics-tab";
import { PortfolioTab } from "@/components/seller/portfolio-tab";
import { useTranslation } from "@/i18n";

const searchSchema = z.object({
  tab: z
    .enum(["overview", "stripe", "auctions", "fulfilment", "sales", "earnings", "performance", "analytics", "portfolio", "notifications"])
    .optional(),
  status: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/seller/")({
  head: () => ({
    meta: [{ title: "Seller Dashboard — BidNova Auctions" }],
  }),
  validateSearch: searchSchema,
  component: SellerDashboard,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function SellerDashboard() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const active = tab ?? "overview";
  const { t } = useTranslation("settings");

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">{t("sellerIndex.eyebrow", "Seller")}</p>
          <h1 className="mt-1 font-display text-4xl sm:text-5xl">
            <span className="text-gold-gradient">{t("sellerIndex.title", "Dashboard")}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {t("sellerIndex.subtitle", "Manage your listings, sales, payouts and performance in one place.")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/seller/shipped">
              <Package className="h-4 w-4 mr-1" /> {t("sellerIndex.shippedHistoryBtn", "Shipped history")}
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/seller/shipping">
              <Truck className="h-4 w-4 mr-1" /> {t("sellerIndex.shippingSettingsBtn", "Shipping settings")}
            </Link>
          </Button>
        </div>
      </header>

      <Tabs
        value={active}
        onValueChange={(v) => navigate({ to: "/seller", search: { tab: v as "overview" } })}
      >
        <TabsList className="mb-6 flex flex-wrap justify-start h-auto">
          <TabsTrigger value="overview">{t("sellerIndex.tabOverview", "Overview")}</TabsTrigger>
          <TabsTrigger value="stripe">{t("sellerIndex.tabStripe", "Stripe")}</TabsTrigger>
          <TabsTrigger value="auctions">{t("sellerIndex.tabAuctions", "Auctions")}</TabsTrigger>
          <TabsTrigger value="fulfilment">{t("sellerIndex.tabFulfilment", "Fulfilment")}</TabsTrigger>
          <TabsTrigger value="sales">{t("sellerIndex.tabSales", "Sales")}</TabsTrigger>
          <TabsTrigger value="earnings">{t("sellerIndex.tabEarnings", "Earnings")}</TabsTrigger>
          <TabsTrigger value="performance">{t("sellerIndex.tabPerformance", "Performance")}</TabsTrigger>
          <TabsTrigger value="analytics">{t("sellerIndex.tabAnalytics", "Analytics")}</TabsTrigger>
          <TabsTrigger value="registry">{t("sellerIndex.tabRegistry", "Registry")}</TabsTrigger>
          <TabsTrigger value="portfolio">{t("sellerIndex.tabPortfolio", "Portfolio")}</TabsTrigger>
          <TabsTrigger value="notifications">{t("sellerIndex.tabNotifications", "Notifications")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="stripe"><StripeTab /></TabsContent>
        <TabsContent value="auctions"><AuctionsShortcut /></TabsContent>
        <TabsContent value="fulfilment"><FulfilmentTab /></TabsContent>
        <TabsContent value="sales"><SalesShortcut /></TabsContent>
        <TabsContent value="earnings"><EarningsTab /></TabsContent>
        <TabsContent value="performance"><PerformanceTab /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
        <TabsContent value="registry"><SellerRegistryTab /></TabsContent>
        <TabsContent value="portfolio"><PortfolioTab /></TabsContent>
        <TabsContent value="notifications"><NotificationsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------ Overview ------------------------------- */

function OverviewTab() {
  const { t } = useTranslation("settings");
  const fetchOverview = useServerFn(getSellerOverview);
  const fetchWatchers = useServerFn(getSellerWatcherCounts);
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["seller-overview"],
    queryFn: () => fetchOverview({}),
  });
  const { data: watchers } = useQuery({
    queryKey: ["seller-watcher-counts"],
    queryFn: () => fetchWatchers({}),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  const kpis: Array<{
    label: string;
    value: string;
    icon: typeof Coins;
    hint?: string;
    to?: { tab: "auctions"; status?: string };
    tone?: "warn";
  }> = [
    { label: t("sellerIndex.kpi.totalSales", "Total sales"), value: formatGBP(data.grossPence), icon: Coins, hint: t("sellerIndex.kpi.totalSalesHint", "Paid + shipped + delivered + released") },
    { label: t("sellerIndex.kpi.auctionRevenue", "Auction revenue"), value: formatGBP(data.auctionRevenuePence), icon: Gauge, hint: t("sellerIndex.kpi.auctionRevenueHint", "Sales from auction wins") },
    { label: t("sellerIndex.kpi.binRevenue", "Buy It Now revenue"), value: formatGBP(data.binRevenuePence), icon: Sparkles, hint: t("sellerIndex.kpi.binRevenueHint", "{count, plural, one {# BIN order} other {# BIN orders}}", { count: data.binOrdersCount }) },
    { label: t("sellerIndex.kpi.binConversionRate", "BIN conversion rate"), value: `${(data.binConversionRate * 100).toFixed(1)}%`, icon: TrendingUp, hint: t("sellerIndex.kpi.binConversionHint", "{sold} sold / {listings} listings", { sold: data.binOrdersCount, listings: data.binListingsCount }) },
    { label: t("sellerIndex.kpi.binAvgPrice", "BIN average price"), value: formatGBP(data.binAvgSellingPricePence), icon: Coins, hint: t("sellerIndex.kpi.binAvgPriceHint", "Avg BIN selling price") },
    { label: t("sellerIndex.kpi.activeBinListings", "Active BIN listings"), value: String(data.activeBinListings), icon: Sparkles, hint: t("sellerIndex.kpi.activeBinListingsHint", "Currently available for BIN") },
    { label: t("sellerIndex.kpi.expiredBinListings", "Expired BIN listings"), value: String(data.expiredBinListings), icon: Timer, hint: t("sellerIndex.kpi.expiredBinListingsHint", "BIN-enabled but ended or sold") },
    { label: t("sellerIndex.kpi.reservationsExpired", "Reservations expired"), value: String(data.reservationExpiredCount), icon: XCircle, hint: t("sellerIndex.kpi.reservationsExpiredHint", "BIN checkouts abandoned") },
    { label: t("sellerIndex.kpi.netEarnings", "Net earnings"), value: formatGBP(data.netPence), icon: TrendingUp, hint: t("sellerIndex.kpi.netEarningsHint", "After 8% platform fee") },
    { label: t("sellerIndex.kpi.heldBalance", "Held balance"), value: formatGBP(data.heldPence), icon: Timer, hint: t("sellerIndex.kpi.heldBalanceHint", "Awaiting delivery confirmation") },
    { label: t("sellerIndex.kpi.availablePayout", "Available for payout"), value: formatGBP(data.releasedPence), icon: Wallet, hint: t("sellerIndex.kpi.availablePayoutHint", "Released to your Stripe account") },
    { label: t("sellerIndex.kpi.platformFees", "Platform fees"), value: formatGBP(data.feesPence), icon: BadgeCheck },
    { label: t("sellerIndex.kpi.activeAuctions", "Active auctions"), value: String(data.active), icon: Sparkles, to: { tab: "auctions", status: "active" } },
    { label: t("sellerIndex.kpi.totalWatchers", "Total watchers"), value: String(watchers?.totalActive ?? 0), icon: Eye, hint: t("sellerIndex.kpi.totalWatchersHint", "Across your active auctions") },
    { label: t("sellerIndex.kpi.endingSoon", "Ending in 24h"), value: String(data.endingSoon), icon: Timer },
    { label: t("sellerIndex.kpi.pendingApproval", "Pending approval"), value: String(data.pending), icon: ClipboardList, hint: t("sellerIndex.kpi.pendingApprovalHint", "Awaiting moderator review"), to: { tab: "auctions", status: "pending" } },
    { label: t("sellerIndex.kpi.rejected", "Rejected"), value: String(data.rejected), icon: XCircle, tone: data.rejected > 0 ? "warn" : undefined, to: { tab: "auctions", status: "rejected" } },
    { label: t("sellerIndex.kpi.awaitingShipment", "Awaiting shipment"), value: String(data.awaitingShipment), icon: Package, hint: t("sellerIndex.kpi.awaitingShipmentHint", "Ready to post") },
    { label: t("sellerIndex.kpi.completedOrders", "Completed orders"), value: String(data.completed), icon: BadgeCheck },
  ];

  return (
    <div className="space-y-6">
      <SellerHeroGrid />

      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
          {isRefetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
          <span className="ml-2">{t("sellerIndex.refresh", "Refresh")}</span>
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {kpis.map((k) => {
          const toneClass = k.tone === "warn" ? "border-amber-500/40 bg-amber-500/5" : "border-border/60 bg-card";
          const content = (
            <>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-surface-2 p-2"><k.icon className="h-4 w-4 text-gold" /></div>
                <span className="text-sm text-muted-foreground">{k.label}</span>
              </div>
              <div className="mt-3 font-display text-3xl">{k.value}</div>
              {k.hint && <p className="mt-1 text-xs text-muted-foreground">{k.hint}</p>}
            </>
          );
          if (k.to) {
            return (
              <Link
                key={k.label}
                to="/seller"
                search={{ tab: k.to.tab, status: k.to.status }}
                className={`rounded-xl border p-5 gold-ring block text-left hover:border-gold/60 transition-colors ${toneClass}`}
              >
                {content}
              </Link>
            );
          }
          return (
            <div key={k.label} className={`rounded-xl border p-5 gold-ring ${toneClass}`}>
              {content}
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-5">
        <h3 className="font-display text-2xl mb-4">{t("sellerIndex.monthlySales", "Monthly sales")}</h3>
        {data.monthly.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("sellerIndex.monthlySalesEmpty", "No sales yet — your first order will appear here.")}</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly.map((m) => ({ month: m.month, total: m.total / 100 }))}>
                <defs>
                  <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d4af37" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#d4af37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" stroke="#888" fontSize={12} />
                <YAxis stroke="#888" fontSize={12} tickFormatter={(v) => `£${v}`} />
                <ChartTooltip
                  contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8 }}
                  formatter={(v: number) => [`£${v.toFixed(2)}`, t("sellerIndex.salesTooltipLabel", "Sales")]}
                />
                <Area type="monotone" dataKey="total" stroke="#d4af37" fill="url(#gold)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <RecentActivity />
    </div>
  );
}

function RecentActivity() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["seller-recent-activity", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_audit_log")
        .select("event_type,created_at,order_id,metadata,orders!inner(seller_id,auction_id,auctions(title))")
        .eq("orders.seller_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(15);
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5">
      <h3 className="font-display text-2xl mb-4">{t("sellerIndex.recentActivity", "Recent activity")}</h3>
      {(!data || data.length === 0) ? (
        <p className="text-sm text-muted-foreground">{t("sellerIndex.recentActivityEmpty", "No activity yet.")}</p>
      ) : (
        <ul className="space-y-2">
          {data.map((row, i) => (
            <li key={i} className="flex items-center justify-between text-sm border-b border-border/40 last:border-0 py-2">
              <span className="text-foreground/80">
                <span className="font-medium">{row.event_type.replace(/^seller:/, "")}</span>
                {row.orders?.auctions?.title && <span className="text-muted-foreground"> · {row.orders.auctions.title}</span>}
              </span>
              <span className="text-xs text-muted-foreground">{new Date(row.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------- Stripe -------------------------------- */

function StripeTab() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const openExpress = useServerFn(createExpressDashboardLink);
  const [loading, setLoading] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["seller-stripe-profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("stripe_account_id,stripe_charges_enabled,stripe_payouts_enabled,stripe_details_submitted")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const masked = profile?.stripe_account_id
    ? `${profile.stripe_account_id.slice(0, 6)}••••${profile.stripe_account_id.slice(-4)}`
    : null;

  const handleOpen = async () => {
    try {
      setLoading(true);
      const res = await openExpress({});
      if (res?.url) window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <StripeConnectCard />

      {profile?.stripe_account_id && (
        <div className="rounded-xl border border-border/60 bg-card p-6">
          <h3 className="font-display text-2xl mb-2">{t("sellerIndex.stripeAccountTitle", "Stripe Express account")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("sellerIndex.connectedAccount", "Connected account")}: <span className="font-mono text-foreground">{masked}</span>
          </p>
          <div className="mt-4">
            <Button
              onClick={handleOpen}
              disabled={loading || !profile.stripe_charges_enabled}
              className="bg-gold-gradient hover:opacity-90"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              {t("sellerIndex.openExpressDashboard", "Open Stripe Express Dashboard")}
            </Button>
            {!profile.stripe_charges_enabled && (
              <p className="text-xs text-amber-400 mt-2">
                {t("sellerIndex.finishOnboarding", "Finish Stripe onboarding to open your Express dashboard and receive payouts.")}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Auctions/Sales pointers ------------------ */

type AuctionSortKey = "ends" | "highest" | "bids" | "watchers" | "created";
type AuctionSortDir = "asc" | "desc";

const defaultAuctionSort: { key: AuctionSortKey; dir: AuctionSortDir } = {
  key: "created",
  dir: "desc",
};

function formatTimeRemaining(endTime: string, t: (key: string, fallback: string) => string) {
  const diff = new Date(endTime).getTime() - Date.now();
  if (diff <= 0) return t("sellerIndex.ended", "Ended");
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function AuctionsShortcut() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const { status: statusFilter } = Route.useSearch();
  const navigate = useNavigate();
  const fetchWatchers = useServerFn(getSellerWatcherCounts);
  const [sort, setSort] = useState<{ key: AuctionSortKey; dir: AuctionSortDir }>(defaultAuctionSort);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["seller-auctions-list", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("auctions")
        .select("id,title,status,end_time,bid_count,current_bid_pence,starting_price_pence,created_at")
        .eq("seller_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: watchers } = useQuery({
    queryKey: ["seller-watcher-counts"],
    queryFn: () => fetchWatchers({}),
  });

  const handleSort = (key: AuctionSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : { key, dir: key === "ends" ? "asc" : "desc" },
    );
  };

  const list = useMemo(() => {
    let base = (rows ?? []).map((r) => ({
      ...r,
      watcherCount: watchers?.perAuction?.[r.id] ?? 0,
    }));
    if (statusFilter) base = base.filter((r) => r.status === statusFilter);

    base.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "ends":
          cmp = new Date(a.end_time).getTime() - new Date(b.end_time).getTime();
          break;
        case "highest":
          cmp = (a.current_bid_pence ?? 0) - (b.current_bid_pence ?? 0);
          break;
        case "bids":
          cmp = (a.bid_count ?? 0) - (b.bid_count ?? 0);
          break;
        case "watchers":
          cmp = a.watcherCount - b.watcherCount;
          break;
        case "created":
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });

    return base;
  }, [rows, watchers, statusFilter, sort]);

  const SortHeader = ({
    label,
    sortKey,
    icon: Icon,
  }: {
    label: string;
    sortKey: AuctionSortKey;
    icon?: typeof Eye;
  }) => {
    const active = sort.key === sortKey;
    const IconComp = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => handleSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-gold"
        aria-label={t("sellerIndex.sortByAria", "Sort by {label}", { label: label.toLowerCase() })}
      >
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
        <IconComp className="h-3.5 w-3.5" />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border/60 bg-card p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-2xl">{t("sellerIndex.yourAuctions", "Your auctions")}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t("sellerIndex.yourAuctionsDesc", "Manage listings, edit non-bid fields, cancel or relist ended items.")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-gold-gradient hover:opacity-90">
              <Link to="/my-auctions">{t("sellerIndex.openAuctionsManager", "Open auctions manager")}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/sell">{t("sellerIndex.listNewCard", "List a new card")}</Link>
            </Button>
          </div>
        </div>
        {statusFilter && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-muted-foreground">{t("sellerIndex.filteredByStatus", "Filtered by status:")}</span>
            <Badge variant="outline" className="capitalize">{statusFilter}</Badge>
            <button
              type="button"
              onClick={() => navigate({ to: "/seller", search: { tab: "auctions" } })}
              className="text-gold hover:underline"
            >
              {t("sellerIndex.clearFilter", "Clear filter")}
            </button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {t("sellerIndex.bidLockNote", "Once bids exist on an auction, only description and photos can be edited — price and end time are locked.")}
        </p>
      </div>

      <div className="rounded-xl border border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[160px]">{t("sellerIndex.colAuction", "Auction")}</TableHead>
                <TableHead>{t("sellerIndex.colStatus", "Status")}</TableHead>
                <TableHead className="text-right">
                  <SortHeader label={t("sellerIndex.colEnds", "Ends")} sortKey="ends" icon={Timer} />
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader label={t("sellerIndex.colBids", "Bids")} sortKey="bids" />
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader label={t("sellerIndex.colWatchers", "Watchers")} sortKey="watchers" icon={Eye} />
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader label={t("sellerIndex.colHighestBid", "Highest bid")} sortKey="highest" />
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader label={t("sellerIndex.colListed", "Listed")} sortKey="created" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">{t("sellerIndex.loading", "Loading…")}</TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    {t("sellerIndex.noListings", "No listings yet.")}
                  </TableCell>
                </TableRow>
              ) : (
                list.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="min-w-[160px]">
                      <Link
                        to="/auction/$slug"
                        params={{ slug: buildAuctionSlug(a as never) }}
                        className="hover:text-gold"
                      >
                        {a.title}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{a.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{formatTimeRemaining(a.end_time, t)}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.bid_count ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.watcherCount}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {a.bid_count ? formatGBP(a.current_bid_pence ?? 0) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {new Date(a.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function SalesShortcut() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();

  const { data: orders, isLoading } = useQuery({
    queryKey: ["seller-order-status-counts", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id,status")
        .eq("seller_id", user!.id);
      if (error) throw error;
      return (data ?? []) as { id: string; status: string }[];
    },
  });

  const pipeline = useMemo(() => {
    const list = orders ?? [];
    return [
      { label: t("sellerIndex.stageAwaitingPayment", "Awaiting Payment"), statuses: ["pending_payment"], icon: Clock },
      { label: t("sellerIndex.stagePaid", "Paid"), statuses: ["paid"], icon: CreditCard },
      { label: t("sellerIndex.stageReadyToShip", "Ready to Ship"), statuses: ["ready_to_ship", "exported_to_click_drop"], icon: Package },
      { label: t("sellerIndex.stageShipped", "Shipped"), statuses: ["shipped"], icon: Truck },
      { label: t("sellerIndex.stageDelivered", "Delivered"), statuses: ["delivered"], icon: CheckCircle2 },
      { label: t("sellerIndex.stageReleased", "Released"), statuses: ["released"], icon: BanknoteArrowUp },
    ].map((stage) => ({
      ...stage,
      count: list.filter((o) => stage.statuses.includes(o.status)).length,
      statusParam: stage.statuses.join(","),
    }));
  }, [orders]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border/60 bg-card p-6 space-y-4">
        <div>
          <h3 className="font-display text-2xl">{t("sellerIndex.salesShipping", "Sales & shipping")}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t("sellerIndex.salesShippingDesc", "Full order pipeline: paid → ready → exported to Click & Drop → shipped → delivered → payout released. Bulk CSV export, mark-as-shipped with tracking, and Royal Mail links are all in the orders workspace.")}
          </p>
        </div>
        <Button asChild className="bg-gold-gradient hover:opacity-90">
          <Link to="/orders">{t("sellerIndex.openOrdersWorkspace", "Open orders workspace")}</Link>
        </Button>
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-6 space-y-4">
        <div>
          <h3 className="font-display text-2xl">{t("sellerIndex.salesPipeline", "Sales pipeline")}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t("sellerIndex.salesPipelineDesc", "Click any stage to view matching orders in the orders workspace.")}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {pipeline.map((stage) => (
            <Link
              key={stage.label}
              to="/orders"
              search={{ status: stage.statusParam }}
              className="rounded-xl border border-border/60 bg-surface-2/60 p-4 hover:border-gold/60 transition-colors block"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-surface-2 p-2">
                  <stage.icon className="h-4 w-4 text-gold" />
                </div>
                <span className="text-sm text-muted-foreground">{stage.label}</span>
              </div>
              <div className="mt-3 font-display text-3xl">
                {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : stage.count}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Earnings ------------------------------- */

function EarningsTab() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const [filterPending, setFilterPending] = useState(false);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["seller-earnings", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(
          "id,status,amount_pence,commission_pence,seller_payout_pence,shipping_cost_pence,payout_released_at,payout_reference,created_at,paid_at,has_active_dispute,auctions(title)",
        )
        .eq("seller_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const totals = useMemo(() => {
    const list = data ?? [];
    let gross = 0, fees = 0, shipping = 0, refunds = 0, net = 0, held = 0, released = 0;
    let disputes = 0;
    for (const o of list) {
      if (o.status === "refunded") refunds += o.amount_pence ?? 0;
      if (o.has_active_dispute) disputes++;
      if (["paid", "ready_to_ship", "exported_to_click_drop", "shipped", "delivered", "released"].includes(o.status)) {
        gross += o.amount_pence ?? 0;
        fees += o.commission_pence ?? 0;
        shipping += o.shipping_cost_pence ?? 0;
        net += o.seller_payout_pence ?? 0;
      }
      if (o.status === "released") released += o.seller_payout_pence ?? 0;
      else if (["paid", "ready_to_ship", "exported_to_click_drop", "shipped", "delivered"].includes(o.status)) {
        held += o.seller_payout_pence ?? 0;
      }
    }
    return { gross, fees, shipping, refunds, net, held, released, disputes };
  }, [data]);

  const downloadStatement = () => {
    const rows = [
      [
        t("sellerIndex.csvOrder", "Order"),
        t("sellerIndex.csvAuction", "Auction"),
        t("sellerIndex.csvDate", "Date"),
        t("sellerIndex.csvStatus", "Status"),
        t("sellerIndex.csvGross", "Gross"),
        t("sellerIndex.csvFee", "Fee"),
        t("sellerIndex.csvShipping", "Shipping"),
        t("sellerIndex.csvNet", "Net"),
        t("sellerIndex.csvReleased", "Released"),
        t("sellerIndex.csvReference", "Reference"),
      ],
    ];
    for (const o of data ?? []) {
      rows.push([
        o.id.slice(0, 8),
        (o.auctions as { title: string } | null)?.title ?? "",
        (o.paid_at ?? o.created_at).slice(0, 10),
        o.status,
        ((o.amount_pence ?? 0) / 100).toFixed(2),
        ((o.commission_pence ?? 0) / 100).toFixed(2),
        ((o.shipping_cost_pence ?? 0) / 100).toFixed(2),
        ((o.seller_payout_pence ?? 0) / 100).toFixed(2),
        o.payout_released_at ? o.payout_released_at.slice(0, 10) : "",
        o.payout_reference ?? "",
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `seller-statement-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock message={(error as Error).message} onRetry={() => refetch()} />;

  const PAID_STATUSES = ["paid", "ready_to_ship", "exported_to_click_drop", "shipped", "delivered"];
  const pendingPayouts = (data ?? []).filter(
    (o) =>
      PAID_STATUSES.includes(o.status) &&
      !o.has_active_dispute &&
      !o.payout_released_at,
  );
  const pendingTotal = pendingPayouts.reduce((s, o) => s + (o.seller_payout_pence ?? 0), 0);
  const released = (data ?? []).filter((o) => o.status === "released");
  const showingPending = filterPending && pendingPayouts.length > 0;
  const historyRows = showingPending ? pendingPayouts : released;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label={t("sellerIndex.tileGrossSales", "Gross sales")} value={formatGBP(totals.gross)} />
        <Tile label={t("sellerIndex.tilePlatformFees", "Platform fees")} value={formatGBP(totals.fees)} />
        <Tile label={t("sellerIndex.tileShippingReceived", "Shipping received")} value={formatGBP(totals.shipping)} />
        <Tile label={t("sellerIndex.tileRefunds", "Refunds")} value={formatGBP(totals.refunds)} />
        <Tile label={t("sellerIndex.tileNetEarnings", "Net earnings")} value={formatGBP(totals.net)} tone="gold" />
        <Tile label={t("sellerIndex.tileHeldBalance", "Held balance")} value={formatGBP(totals.held)} />
        <Tile label={t("sellerIndex.tileReleasedPayout", "Released to payout")} value={formatGBP(totals.released)} tone="gold" />
        <Tile label={t("sellerIndex.tileActiveDisputes", "Active disputes")} value={String(totals.disputes)} tone={totals.disputes > 0 ? "warn" : undefined} />
      </div>

      <button
        type="button"
        onClick={() => setFilterPending((v) => !v)}
        className={`w-full text-left rounded-xl border p-5 transition-colors ${
          showingPending ? "border-primary bg-primary/5" : "border-border/60 bg-card hover:border-primary/60"
        }`}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm text-muted-foreground">{t("sellerIndex.nextExpectedPayout", "Next expected payout")}</div>
            <div className="font-display text-3xl mt-1">{formatGBP(pendingTotal)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {t("sellerIndex.ordersPendingRelease", "{count, plural, one {# order pending release} other {# orders pending release}}", { count: pendingPayouts.length })}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {showingPending ? t("sellerIndex.showingPendingHint", "Showing pending payouts — click to show released") : t("sellerIndex.clickToViewIncluded", "Click to view included orders")}
          </div>
        </div>
      </button>

      <div className="rounded-xl border border-border/60 bg-card p-5">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h3 className="font-display text-2xl">
            {showingPending ? t("sellerIndex.pendingPayoutsTitle", "Pending payouts") : t("sellerIndex.payoutHistoryTitle", "Payout history")}
          </h3>
          <div className="flex items-center gap-2">
            {showingPending && (
              <Button variant="ghost" size="sm" onClick={() => setFilterPending(false)}>
                {t("sellerIndex.clearFilter", "Clear filter")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={downloadStatement}>{t("sellerIndex.downloadCsv", "Download CSV statement")}</Button>
          </div>
        </div>
        {historyRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {showingPending ? t("sellerIndex.noPendingPayouts", "No pending payouts.") : t("sellerIndex.noReleasedPayouts", "No payouts released yet.")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{showingPending ? t("sellerIndex.colOrderDate", "Order date") : t("sellerIndex.colDate", "Date")}</TableHead>
                <TableHead>{t("sellerIndex.colAuction", "Auction")}</TableHead>
                <TableHead>{t("sellerIndex.colAmount", "Amount")}</TableHead>
                <TableHead>{showingPending ? t("sellerIndex.colStatus", "Status") : t("sellerIndex.colReference", "Reference")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historyRows.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>
                    {showingPending
                      ? (o.paid_at ?? o.created_at).slice(0, 10)
                      : o.payout_released_at?.slice(0, 10)}
                  </TableCell>
                  <TableCell>{(o.auctions as { title: string } | null)?.title ?? "—"}</TableCell>
                  <TableCell>{formatGBP(o.seller_payout_pence)}</TableCell>
                  <TableCell className={showingPending ? "text-xs" : "font-mono text-xs"}>
                    {showingPending ? o.status : (o.payout_reference ?? "—")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- Performance ----------------------------- */

function PerformanceTab() {
  const { t } = useTranslation("settings");
  const fetchPerf = useServerFn(getSellerPerformance);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["seller-performance"],
    queryFn: () => fetchPerf({}),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

  const tiles = [
    { label: t("sellerIndex.perfCompletedSales", "Completed sales"), value: String(data.completed), icon: BadgeCheck },
    { label: t("sellerIndex.perfCancellationRate", "Cancellation rate"), value: pct(data.cancellationRate), icon: Gauge, warn: data.cancellationRate > 0.05 },
    { label: t("sellerIndex.perfLateShipments", "Late shipments (>72h)"), value: pct(data.lateShipmentRate), icon: Timer, warn: data.lateShipmentRate > 0.1 },
    { label: t("sellerIndex.perfAvgDispatch", "Avg dispatch time"), value: `${data.avgDispatchHrs.toFixed(1)}h`, icon: Timer },
    { label: t("sellerIndex.perfDeliveryConfirmed", "Delivery confirmed"), value: pct(data.deliveryConfirmationRate), icon: Package },
    { label: t("sellerIndex.perfOpenDisputes", "Open disputes"), value: String(data.disputesOpen), icon: ShieldAlert, warn: data.disputesOpen > 0 },
    { label: t("sellerIndex.perfAccountWarnings", "Account warnings"), value: String(data.warnings), icon: ShieldAlert, warn: data.warnings > 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border/60 bg-card p-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">{t("sellerIndex.sellerStatus", "Seller status")}</p>
          <p className="mt-1 font-display text-2xl capitalize">{data.status}</p>
        </div>
        <Badge className={data.status === "active" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-red-500/15 text-red-400 border-red-500/30"}>
          {data.status}
        </Badge>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((t) => (
          <div key={t.label} className={`rounded-xl border p-5 ${t.warn ? "border-amber-500/40 bg-amber-500/5" : "border-border/60 bg-card"}`}>
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-surface-2 p-2"><t.icon className="h-4 w-4 text-gold" /></div>
              <span className="text-sm text-muted-foreground">{t.label}</span>
            </div>
            <div className="mt-3 font-display text-3xl">{t.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-dashed border-border/60 bg-card/40 p-5">
        <p className="text-sm text-muted-foreground">
          {t("sellerIndex.feedbackNote", "Feedback score — buyer feedback is coming soon. Your dispatch speed and delivery-confirmation rate above already drive your seller reputation.")}
        </p>
      </div>
    </div>
  );
}

/* ---------------------------- Notifications ---------------------------- */

function NotificationsTab() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const { data, refetch } = useQuery({
    queryKey: ["seller-notifications", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id,type,title,message,created_at,read_at,auction_id")
        .eq("user_id", user!.id)
        .in("type", [
          "auction_ended_seller",
          "payment_received",
          "order_created",
          "order_shipped",
          "delivery_confirmed",
          "payout_released",
          "dispute_opened",
          "seller_suspended",
          "seller_unsuspended",
          "listing_approved",
          "listing_rejected",
          "photos_requested",
        ])
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });

  const markAllRead = async () => {
    if (!user) return;
    const ids = (data ?? []).filter((n) => !n.read_at).map((n) => n.id);
    if (ids.length === 0) return;
    const { error } = await supabase.from("notifications").update({ read_at: new Date().toISOString() }).in("id", ids);
    if (error) toast.error(error.message);
    else refetch();
  };

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-gold" />
          <h3 className="font-display text-2xl">{t("sellerIndex.sellerNotifications", "Seller notifications")}</h3>
        </div>
        <Button variant="outline" size="sm" onClick={markAllRead}>{t("sellerIndex.markAllRead", "Mark all as read")}</Button>
      </div>
      {(!data || data.length === 0) ? (
        <p className="text-sm text-muted-foreground">{t("sellerIndex.allCaughtUp", "You're all caught up.")}</p>
      ) : (
        <ul className="divide-y divide-border/40">
          {data.map((n) => (
            <li key={n.id} className={`py-3 flex items-start justify-between gap-4 ${!n.read_at ? "opacity-100" : "opacity-70"}`}>
              <div>
                <p className="text-sm font-medium">
                  {!n.read_at && <span className="inline-block w-2 h-2 rounded-full bg-gold mr-2 align-middle" />}
                  {n.title}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{n.message}</p>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------- Shared -------------------------------- */

function Tile({ label, value, tone, hint }: { label: string; value: string; tone?: "gold" | "warn"; hint?: string }) {
  const border = tone === "gold" ? "border-gold/40" : tone === "warn" ? "border-amber-500/40 bg-amber-500/5" : "border-border/60";
  return (
    <div className={`rounded-xl border ${border} bg-card p-5`}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`mt-2 font-display text-2xl ${tone === "gold" ? "text-gold-gradient" : ""}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation("settings");
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm">
      <p className="font-medium text-red-400">{t("sellerIndex.somethingWrong", "Something went wrong")}</p>
      <p className="text-muted-foreground mt-1">{message}</p>
      <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        <RefreshCcw className="h-4 w-4 mr-2" /> {t("sellerIndex.retry", "Retry")}
      </Button>
    </div>
  );
}
